<template>
  <div class="dashboard">
    <div class="header">
      <h1>{{ $t('Programs') }}</h1>
      <p class="plan" :class="{ pro: authStore.isPro }">
        <template v-if="tier === 'pro_plus'">
          {{ $t('Pro Plus plan: every program is open, the finite element ones included.') }}
          <router-link to="/pricing">{{ $t('Manage plan') }}</router-link>
        </template>
        <template v-else-if="tier === 'plus'">
          {{ $t('Plus plan: Kinematic and Liquefaction are open besides the free programs.') }}
          <router-link to="/pricing">{{ $t('Manage plan') }}</router-link>
        </template>
        <template v-else-if="authStore.isPro">
          {{ $t('Pro plan: the Plus programs and Pile Capacity and Pile Wall Analysis are open.') }}
          <router-link to="/pricing">{{ $t('Manage plan') }}</router-link>
        </template>
        <template v-else>
          {{ $t('Free plan: Limit Equilibrium, Bearing Capacity, Settlement and Soil Correlations. Every program has saving, reports and exports.') }}
          <router-link to="/pricing">{{ $t('See plans') }}</router-link>
        </template>
      </p>
    </div>

    <div v-if="!authStore.isVerified" class="verify-banner">
      <strong>{{ $t('Confirm your e-mail.') }}</strong>
      {{ $t('We sent a link to') }} {{ authStore.user?.email }}. {{ $t('Until you open it the programs and upgrades stay locked.') }}
      <button :disabled="resending" @click="resend">{{ resending ? $t('Sending...') : $t('Send it again') }}</button>
      <button @click="refreshUser">{{ $t('I have confirmed') }}</button>
      <span v-if="verifyMessage" class="verify-message">{{ verifyMessage }}</span>
    </div>

    <p v-if="error" class="error-message">{{ error }}</p>

    <section v-for="group in GROUPS" :key="group.key" class="group" :class="`g-${group.key}`">
      <h2><span class="dot"></span>{{ $t(group.title) }}</h2>
      <div class="grid">
        <div v-for="key in group.keys" :key="key" class="item">
          <component
            :is="PROGRAMS[key].ready ? 'button' : 'div'"
            class="card"
            :class="{ soon: !PROGRAMS[key].ready, locked: isLocked(key) }"
            :disabled="opening === key"
            @click="PROGRAMS[key].ready && open(key)"
          >
            <span class="badge">{{ PROGRAMS[key].short }}</span>
            <span v-if="PROGRAMS[key].ready && isLocked(key)" class="plan-chip" :title="$t('Needs a higher plan')">
              🔒 {{ requiredLabel(key) }}
            </span>
            <strong>{{ $t(PROGRAMS[key].name) }}</strong>
            <span class="summary">{{ $t(PROGRAMS[key].summary) }}</span>
            <span class="state">
              {{ !PROGRAMS[key].ready ? $t('Coming soon') : isLocked(key) ? $t('See plans') : opening === key ? $t('Opening...') : $t('New project') }}
            </span>
          </component>
          <button
            v-if="PROGRAMS[key].ready && TUTORIALS[key]"
            class="tutorial"
            :title="`${$t('Tutorial:')} ${$t(PROGRAMS[key].name)}`"
            :aria-label="`${$t('Tutorial:')} ${$t(PROGRAMS[key].name)}`"
            @click="showTutorial = key"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M2 9l10-5 10 5-10 5z" />
              <path d="M6 11.2V16c0 1.2 2.7 3 6 3s6-1.8 6-3v-4.8" />
              <path d="M22 9v6" />
            </svg>
            <span>{{ $t('Tutorial') }}</span>
          </button>
        </div>
      </div>
    </section>

    <div v-if="showTutorial" class="backdrop" @click.self="showTutorial = ''">
      <div
        class="dialog"
        :class="`g-${groupOf(showTutorial)}`"
        role="dialog"
        aria-modal="true"
        :aria-label="`${$t('Tutorial:')} ${$t(PROGRAMS[showTutorial].name)}`"
      >
        <button class="close" :aria-label="$t('Close')" @click="showTutorial = ''">×</button>
        <span class="badge">{{ PROGRAMS[showTutorial].short }}</span>
        <h3>{{ $t(PROGRAMS[showTutorial].name) }}</h3>
        <p class="intro">{{ guide.intro }}</p>
        <ol>
          <li v-for="([title, text], index) in guide.steps" :key="index">
            <strong>{{ title }}.</strong> {{ text }}
          </li>
        </ol>
        <p class="tip"><strong>{{ $t('Tip.') }}</strong> {{ guide.tip }}</p>
        <div class="actions">
          <button class="primary" :disabled="opening === showTutorial" @click="open(showTutorial)">
            {{ $t('New project') }}
          </button>
          <button :disabled="opening === showTutorial" @click="open(showTutorial, TUTORIALS[showTutorial].example)">
            {{ $t('Open with the example') }}
          </button>
          <a :href="TUTORIALS[showTutorial].docs" target="_blank" rel="noopener">{{ $t('Full documentation') }} ↗</a>
          <a v-if="TUTORIALS[showTutorial].theory !== false" :href="theoryUrl(showTutorial)" target="_blank" rel="noopener">{{ $t('Theory') }} ↗</a>
        </div>
        <p v-if="error" class="error-message">{{ error }}</p>
      </div>
    </div>
    <p v-if="version" class="version">SlipSurface IQ v{{ version }}</p>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import api from '../api'
import { useAuthStore } from '../stores/auth'
import { GROUPS, PROGRAMS } from '../programs'
import { TUTORIALS } from '../tutorials'
import { TUTORIALS_TR } from '../tutorials.tr'
import { lang, t } from '../i18n'

const theoryUrl = (key) => {
  const base = TUTORIALS[key].docs.replace('https://slipsurface.dev/', '')
  return `https://slipsurface.dev/${lang.value === 'tr' ? 'tr/' : ''}${base}theory`
}

const authStore = useAuthStore()
const router = useRouter()

// Which plan opens which program comes from the API, so there is one table for both.
const required = ref({})
const RANK = { free: 0, plus: 1, pro: 2, pro_plus: 3, enterprise: 4 }
const tier = computed(() => authStore.user?.tier || 'free')
const isLocked = (key) => (RANK[tier.value] ?? 0) < (RANK[required.value[key] || 'free'] ?? 0)
const requiredLabel = (key) => ({ plus: 'Plus', pro_plus: 'Pro Plus' }[required.value[key]] || 'Pro')
const guide = computed(() => {
  const en = TUTORIALS[showTutorial.value]
  const tr = lang.value === 'tr' ? TUTORIALS_TR[showTutorial.value] : null
  return tr ? { ...en, ...tr } : en || { intro: '', steps: [], tip: '' }
})
const opening = ref('')
const error = ref('')
const showTutorial = ref('')
const resending = ref(false)
const verifyMessage = ref('')
const version = ref('')

const resend = async () => {
  resending.value = true
  verifyMessage.value = ''
  try {
    const response = await api.post('/api/auth/resend-verification')
    verifyMessage.value = response.data.sent ? t('Sent. Check your inbox and the spam folder.') : t('Already confirmed.')
    if (!response.data.sent) await authStore.fetchUser()
  } catch (err) {
    verifyMessage.value = t(err.response?.data?.detail || 'Could not send the e-mail.')
  } finally {
    resending.value = false
  }
}

const refreshUser = async () => {
  verifyMessage.value = ''
  try {
    await authStore.fetchUser()
    if (!authStore.isVerified) verifyMessage.value = t('Not confirmed yet.')
  } catch (err) {
    verifyMessage.value = t('Could not check. Try again.')
  }
}

onMounted(async () => {
  try {
    required.value = (await api.get('/api/billing/plans')).data.programs || {}
  } catch (err) {
    required.value = {}
  }
  try {
    version.value = (await api.get('/health')).data.version || ''
  } catch (err) {
    version.value = ''
  }
})

const groupOf = (key) => GROUPS.find((g) => g.keys.includes(key))?.key

const onKey = (event) => {
  if (event.key === 'Escape') showTutorial.value = ''
}
onMounted(() => {
  window.addEventListener('keydown', onKey)
  if (authStore.isAuthenticated) authStore.fetchUser().catch(() => {})     // a link opened in another tab
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

const open = async (key, example = null) => {
  if (isLocked(key)) {
    showTutorial.value = ''
    router.push({ path: '/pricing', query: { program: key } })
    return
  }
  opening.value = key
  error.value = ''
  try {
    await authStore.openProgram(key, example)
  } catch (err) {
    opening.value = ''
    error.value = t(err.response?.data?.detail || 'Could not open the program')
  }
}
</script>

<style scoped>
.version {
  margin-top: 32px;
  text-align: center;
  font-size: 12px;
  color: var(--text-3, #8a8578);
}

/* The colour of each group, as in the programs' own interfaces */
.g-slopes { --group: #c6613f; }
.g-rock { --group: #4f6388; }
.g-foundations { --group: #5b7a3a; }
.g-retaining { --group: #9a6a1a; }

.dark .g-slopes { --group: #e07a55; }
.dark .g-rock { --group: #8fa3c8; }
.dark .g-foundations { --group: #9bba78; }
.dark .g-retaining { --group: #d6a44e; }

.dashboard {
  padding: 20px;
}

.header {
  margin-bottom: 24px;
}

h1 {
  margin: 0 0 6px 0;
  font-size: 32px;
}

.plan {
  margin: 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fff3cd;
  color: #856404;
  font-size: 14px;
}

.plan a {
  margin-left: 8px;
  color: inherit;
  font-weight: 600;
}

.plan.pro {
  background: #e8f5e9;
  color: #2e7d32;
}

.dark .plan {
  background: #3a3a2a;
  color: #ffdd99;
}

.dark .plan.pro {
  background: #243324;
  color: #a5d6a7;
}

.verify-banner {
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #e3f2fd;
  color: #0d47a1;
  font-size: 14px;
  line-height: 1.8;
}

.dark .verify-banner {
  background: #1f2f40;
  color: #bbdefb;
}

.verify-banner button {
  margin-left: 8px;
  padding: 3px 10px;
  border: 1px solid currentColor;
  border-radius: 12px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.verify-banner button:disabled {
  opacity: 0.6;
  cursor: default;
}

.verify-message {
  margin-left: 10px;
  font-weight: 600;
}

.error-message {
  padding: 12px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: 14px;
}

.group {
  margin-bottom: 28px;
}

.group h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #777;
}

.group h2 .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--group);
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.card {
  color: #222;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  text-align: left;
  padding: 18px;
  border: 1px solid transparent;
  border-top: 4px solid var(--group);
  border-radius: 10px;
  background: white;
  font: inherit;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.dark .card {
  color: #e8e8e8;
  background: #2a2a2a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.card:hover:not(.soon):not(:disabled) {
  border-color: var(--group);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.card strong {
  font-size: 17px;
}

.badge {
  padding: 2px 8px;
  border-radius: 6px;
  background: color-mix(in srgb, var(--group) 16%, transparent);
  color: var(--group);
  font-size: 12px;
  font-weight: 700;
}

.summary {
  flex: 1;
  color: #666;
  font-size: 13px;
  line-height: 1.45;
}

.dark .summary {
  color: #aaa;
}

.state {
  font-size: 13px;
  font-weight: 600;
  color: var(--group);
}

.plan-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--group, #888);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.card.locked {
  opacity: 0.85;
}

.card.soon {
  cursor: default;
  opacity: 0.55;
  box-shadow: none;
  border: 1px dashed #bbb;
  border-top: 4px solid var(--group);
  background: transparent;
}

.card.soon .state {
  color: #888;
}

.item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item .card {
  flex: 1;
}

.tutorial {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: transparent;
  color: var(--group);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.tutorial:hover,
.tutorial:focus-visible {
  border-color: var(--group);
  background: color-mix(in srgb, var(--group) 10%, transparent);
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
}

.dialog {
  position: relative;
  width: 100%;
  max-width: 620px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px 28px;
  border-top: 5px solid var(--group);
  border-radius: 12px;
  background: white;
  color: inherit;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.dark .dialog {
  background: #2a2a2a;
}

.dialog h3 {
  margin: 8px 0 6px 0;
  font-size: 22px;
}

.dialog .intro {
  margin: 0 0 14px 0;
  color: #666;
}

.dark .dialog .intro {
  color: #aaa;
}

.dialog ol {
  margin: 0 0 14px 0;
  padding-left: 22px;
  font-size: 14px;
  line-height: 1.55;
}

.dialog li {
  margin-bottom: 8px;
}

.dialog .tip {
  margin: 0 0 18px 0;
  padding: 10px 12px;
  border-left: 4px solid var(--group);
  border-radius: 4px;
  background: color-mix(in srgb, var(--group) 10%, transparent);
  font-size: 13px;
}

.dialog .actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.dialog .actions button {
  padding: 9px 16px;
  border: 1px solid var(--group);
  border-radius: 6px;
  background: transparent;
  color: var(--group);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.dialog .actions button.primary {
  background: var(--group);
  color: white;
}

.dialog .actions button:disabled {
  opacity: 0.6;
  cursor: default;
}

.dialog .actions a {
  margin-left: auto;
  color: inherit;
  font-size: 13px;
}

.dialog .close {
  position: absolute;
  top: 10px;
  right: 14px;
  border: none;
  background: transparent;
  color: inherit;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}
</style>
